<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-3">
      <p class="text-sm text-muted-foreground">
        {{ completionLabel }}
      </p>
      <p class="text-xs text-muted-foreground">
        {{ progressLabel }}
      </p>
    </div>

    <p v-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>

    <p v-if="reorderErrorMessage" class="text-sm text-destructive">
      {{ reorderErrorMessage }}
    </p>

    <div v-if="checklistItemsStore.isLoading && sortedChecklistItems.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">Loading checklist items...</div>

    <p v-else-if="!checklistItemsStore.isLoading && sortedChecklistItems.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      No checklist items were found for this checklist.
    </p>

    <VueDraggable
      v-else
      v-model="editableSortedChecklistItems"
      tag="ul"
      class="flex flex-col gap-3"
      handle=".checklist-item-drag-handle"
      :animation="150"
      :disabled="readonly || isReorderingChecklistItems"
      @end="handleChecklistItemSortEnd"
    >
      <template v-for="item in editableSortedChecklistItems" :key="item.id">
        <ChecklistItem
          :item="item"
          :is-not-executable="isNotExecutable"
          :readonly="readonly"
          :is-updating-completion="checklistItemsStore.isUpdatingCompletion(item.id)"
          :is-saving="isSavingChecklistItem(item.id)"
          :is-deleting="checklistItemsStore.isDeleting(item.id)"
          :is-sorting="isReorderingChecklistItems"
          :error-message="itemSaveErrorMessage(item.id)"
          @toggle-completion="({ item: checklistItem, value }) => handleCompletionUpdate(checklistItem, value)"
          @save="saveChecklistItem"
          @edit-notes="openNotesModal"
          @delete="deleteChecklistItem"
        />
      </template>
    </VueDraggable>
  </section>

  <ChecklistItemNotesModal ref="checklistItemNotesModal" :checklist-id="props.checklistId" />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import ChecklistItem from './ChecklistItem.vue'
import ChecklistItemNotesModal from './ChecklistItemNotesModal.vue'
import { useChecklistItemsStore } from '../../stores/checklistItemsStore.js'

const props = defineProps({
  checklistId: {
    type: [Number, String],
    required: true,
  },
  isNotExecutable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const checklistItemsStore = useChecklistItemsStore(`checklist_${props.checklistId}`)
const checklistItemNotesModal = ref(null)
const itemSaveErrorMessages = ref({})
const reorderErrorMessage = ref(null)
const isReorderingChecklistItems = ref(false)
const editableSortedChecklistItems = ref([])

const sortedChecklistItems = computed(() => {
  const items = checklistItemsStore.itemsForChecklist(props.checklistId)

  return [...items].sort((left, right) => {
    const leftOrder = Number(left.order ?? Number.MAX_SAFE_INTEGER)
    const rightOrder = Number(right.order ?? Number.MAX_SAFE_INTEGER)

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }

    return Number(left.id) - Number(right.id)
  })
})

const completedCount = computed(() => {
  return sortedChecklistItems.value.filter((item) => item.completed).length
})

const completionLabel = computed(() => {
  return `${completedCount.value} of ${sortedChecklistItems.value.length} items completed`
})

const progressLabel = computed(() => {
  if (sortedChecklistItems.value.length === 0) {
    return '0%'
  }

  const progress = Math.round((completedCount.value / sortedChecklistItems.value.length) * 100)

  return `${progress}%`
})

const errorMessage = computed(() => {
  if (!checklistItemsStore.error) {
    return null
  }

  return checklistItemsStore.error.response?.data?.message ?? checklistItemsStore.error.message ?? 'Unable to load checklist items.'
})

const loadChecklistItems = async () => {
  await checklistItemsStore.fetchItemsForChecklistOnce(props.checklistId)
}

const synchronizeEditableChecklistItems = () => {
  editableSortedChecklistItems.value = [...sortedChecklistItems.value]
}

const handleCompletionUpdate = async (item, value) => {
  if (props.readonly || props.isNotExecutable) {
    return
  }

  await checklistItemsStore.updateCompletion(item, value === true)
}

const isSavingChecklistItem = (checklistItemId) => {
  return checklistItemsStore.isUpdatingInstruction(checklistItemId) || checklistItemsStore.isUpdatingOrder(checklistItemId) || checklistItemsStore.isUpdatingDescription(checklistItemId)
}

const itemSaveErrorMessage = (checklistItemId) => {
  return itemSaveErrorMessages.value[Number(checklistItemId)] ?? null
}

const clearItemSaveErrorMessage = (checklistItemId) => {
  const normalizedChecklistItemId = Number(checklistItemId)

  if (normalizedChecklistItemId < 1 || !Object.prototype.hasOwnProperty.call(itemSaveErrorMessages.value, normalizedChecklistItemId)) {
    return
  }

  const nextItemSaveErrorMessages = { ...itemSaveErrorMessages.value }
  delete nextItemSaveErrorMessages[normalizedChecklistItemId]
  itemSaveErrorMessages.value = nextItemSaveErrorMessages
}

const saveChecklistItem = async ({ item, instruction, details } = {}) => {
  if (props.readonly) {
    return
  }

  const checklistItemId = Number(item?.id ?? 0)
  if (checklistItemId < 1) {
    return
  }

  clearItemSaveErrorMessage(checklistItemId)

  try {
    await checklistItemsStore.updateInstruction(item, instruction)
    const refreshedChecklistItem = checklistItemsStore.getItemById(checklistItemId)

    await checklistItemsStore.updateDescription(refreshedChecklistItem ?? item, details)
  } catch (error) {
    itemSaveErrorMessages.value = {
      ...itemSaveErrorMessages.value,
      [checklistItemId]: error.response?.data?.message ?? error.message ?? 'Unable to save checklist item.',
    }
  }
}

const deleteChecklistItem = async (item) => {
  if (props.readonly || isReorderingChecklistItems.value) {
    return
  }

  const checklistItemId = Number(item?.id ?? 0)
  if (checklistItemId < 1) {
    return
  }

  if (!window.confirm('Delete this checklist item?')) {
    return
  }

  clearItemSaveErrorMessage(checklistItemId)

  try {
    await checklistItemsStore.deleteItem(checklistItemId)
  } catch (error) {
    itemSaveErrorMessages.value = {
      ...itemSaveErrorMessages.value,
      [checklistItemId]: error.response?.data?.message ?? error.message ?? 'Unable to delete checklist item.',
    }
  }
}

const handleChecklistItemSortEnd = async (event) => {
  if (props.readonly || isReorderingChecklistItems.value) {
    return
  }

  const oldIndex = Number(event?.oldIndex ?? -1)
  const newIndex = Number(event?.newIndex ?? -1)
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    synchronizeEditableChecklistItems()
    return
  }

  reorderErrorMessage.value = null
  isReorderingChecklistItems.value = true

  try {
    const reorderedChecklistItems = [...editableSortedChecklistItems.value]

    for (const [nextOrder, checklistItem] of reorderedChecklistItems.entries()) {
      if (Number(checklistItem?.order ?? -1) === nextOrder) {
        continue
      }

      clearItemSaveErrorMessage(checklistItem.id)

      await checklistItemsStore.updateOrder(checklistItem, nextOrder)
    }
  } catch (error) {
    reorderErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to reorder checklist items.'
  } finally {
    isReorderingChecklistItems.value = false

    try {
      await checklistItemsStore.fetchItemsForChecklist(props.checklistId)
    } catch (error) {
      reorderErrorMessage.value = reorderErrorMessage.value ?? error.response?.data?.message ?? error.message ?? 'Unable to refresh checklist items after reordering.'
    }

    synchronizeEditableChecklistItems()
  }
}

const openNotesModal = (item) => {
  if (props.readonly) {
    return
  }

  checklistItemNotesModal.value?.openModal?.(item)
}

watch(
  () => props.checklistId,
  async () => {
    itemSaveErrorMessages.value = {}
    reorderErrorMessage.value = null
    editableSortedChecklistItems.value = []
    checklistItemNotesModal.value?.closeModal?.()
    await loadChecklistItems()
  },
)

watch(
  sortedChecklistItems,
  () => {
    if (isReorderingChecklistItems.value) {
      return
    }

    synchronizeEditableChecklistItems()
  },
  { immediate: true, deep: true },
)

onMounted(async () => {
  await loadChecklistItems()
})
</script>
